// LoadingSkeleton — generic suspense / async fallback. Used by:
//   - lazy() route splits while chunk fetches
//   - empty-data states before IDB resolves
//   - any place we used to show blank
//
// Decision (Session 19AT · S4 + S9):
//   Animated CSS-only shimmer. No JS animation cost. Three sizes:
//   - 'page'    — full-page (route lazy fallback)
//   - 'section' — sub-section (in-card fallback)
//   - 'inline'  — single line (label fallback)

import type { ReactNode } from 'react';
import { useT } from '../i18n/index.js';

export function LoadingSkeleton({
  variant = 'page',
  label,
}: {
  variant?: 'page' | 'section' | 'inline';
  label?: string;
}): ReactNode {
  const t = useT();
  const loading = t('common.loading');
  const text = label ?? (variant === 'page' ? loading : '');
  return (
    <div
      className={`loading-skeleton loading-skeleton-${variant}`}
      role="status"
      aria-busy="true"
      aria-label={text || loading}
    >
      <div className="loading-skeleton-shimmer" aria-hidden="true" />
      {text && <span className="loading-skeleton-text">{text}</span>}
    </div>
  );
}

export function PageLoadingFallback(): ReactNode {
  return <LoadingSkeleton variant="page" />;
}
